import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { createIRSApplication, getIRSApplication, initSdk, IDRequestStatus } from "@/lib/irs";
import { createApiClient } from "@/lib/Requests";
import { setSingleClickInsightEvent } from "@/lib/util";
import { setIdVerificationStatus } from "@/slices/applicantSlice";
import {
  setAlloyApplicationToken,
  setApplicationEndpointReached,
  setIdVerificationSubmitted,
} from "@/slices/applicationSlice";
import { setLegalComplete } from "@/slices/progressSlice";
import { RootState } from "@/store/dtm";
import SlideTransition from "@/styles/animation/SlideTransition";
import style from "@/styles/pages/VerifyIdentification/VerifyIdentification.module.css";
import { verificationCategory } from "@/types/applicationType";
import { ICreateIRSRequest } from "@/types/irs/idRequestType";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function AcceptIDVerificationTerms() {
  const [consent, setConsent] = useState(false);
  const [initiatingIRS, setInitiatingIRS] = useState(false);
  const [processingIRS, setProcessingIRS] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const credential = useSelector((state: RootState) => state.credential);
  const application = useSelector(
    (state: RootState) => state.application.application
  );

  const homeAddress = useSelector(
    (state: RootState) => state.homeAddress.address
  );
  const mailingAddress = useSelector(
    (state: RootState) => state.mailingAddress.address
  );
  const legalTerms = useSelector(
    (state: RootState) => state.legalTerms.legalTerms
  );

  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const handleConsent = (e: any) => {
    setConsent(e.target.checked);
  };

  async function handleClick() {
    if (!application.skipAlloy) {
      setSingleClickInsightEvent("IDVerificationStart");
      setInitiatingIRS(true);
      await checkIDVStatus();
    } else {
      navigate("/verify-id/upload-complete");
    }
  }

  const checkIDVStatus = async () => {
    //Check the IDV Status in the table for all applications of this deal
    //If at least one application has a hardfail, prevent user from proceeding
    await client.apiIdverificationIdverificationapplicant().then(async idVerification => {
      if (idVerification && idVerification.status == "Hardfail") {
        dispatch(setIdVerificationStatus(verificationCategory.Hardfail));
        dispatch(setApplicationEndpointReached(true));
        navigate("/single/application-error");
      } else {
        await initIDVerification();
      }
    }).catch((error) => {
      navigate("/error/3");
    });
  }


  const initIDVerification = async () => {
    try {
      setInitiatingIRS(true);

      if (!application.alloyApplicationToken) {
        await handleNewApplication();
      } else {
        await handleExistingApplication();
      }
    } catch (error) {
      console.error("Error during ID verification initialization:", error);
      navigate("/error/3");
    } finally {
      setInitiatingIRS(false);
    }
  };

  const handleNewApplication = async () => {
    const idRequest = buildIdRequest();
    const response = await createIRSApplication(idRequest);

    if (!response) {
      throw new Error("Failed to create IRS application");
    }

    dispatch(setAlloyApplicationToken(response.applicationToken));

    if (shouldLaunchSdk(application.blResult, response.idRequestStatus)) {
      await handleSdkInit();
    } else if (response?.idRequestStatus.toLowerCase() === IDRequestStatus.ApplicationReview) {
      setProcessingIRS(true);
      updateStatusToProcessing();
      clearInterval(intervalId);
      submitIDVerification();
      navigate("/verify-id/upload-complete");
    } else {
      handleHardFail();
    }
  };

  const handleExistingApplication = async () => {
    if (applicant.idVerificationStatus === verificationCategory.Hardfail) {
      handleHardFail();
      return;
    }

    const state = await getIRSApplication(applicant.alloySessionId);

    if (shouldLaunchSdk(application.blResult, state?.idRequestStatus)) {
      await handleSdkInit();
    } else if (state?.idRequestStatus.toLowerCase() === IDRequestStatus.ApplicationReview) {
      setProcessingIRS(true);
      updateStatusToProcessing();
      clearInterval(intervalId);
      submitIDVerification();
      navigate("/verify-id/upload-complete");
    } else {
      handleHardFail();
    }
  };

  const buildIdRequest = (): ICreateIRSRequest => {
    const addressList = [homeAddress];
    if (mailingAddress.postalCode) {
      addressList.push(mailingAddress);
    }

    return {
      applicant,
      credential: credential.credential,
      additionalData: {
        blocklist: application.blResult,
        phoneverified: applicant.twilioResult.codeVerified,
        isprospect: true
      },
      addresses: addressList,
    };
  };

  const shouldLaunchSdk = (blocklist: boolean, idRequestStatus: string): boolean => {
    return !blocklist && (idRequestStatus.toLowerCase() !== IDRequestStatus.Denied && idRequestStatus.toLowerCase() !== IDRequestStatus.ApplicationReview);
  };

  const handleHardFail = () => {
    clearInterval(intervalId);
    updateStatusToProcessing();
    submitIDVerification();
    navigate("/single/application-error");
  };

  const handleSdkInit = async () => {
    if (!application.idVerificationSubmitted) {
      await initSdk(applicant.alloySessionId);
    } else {
      navigate("/verify-id/upload-complete");
    }
  }

  const submitIDVerification = async () => {
    dispatch(setIdVerificationSubmitted(true));
    await client.apiIdverificationProcess(applicant.alloySessionId, application.id)
      .then(() => {
        saveApplicationData();
        setProcessingIRS(false);
        return
      })
      .catch((error) => {
        console.log(error);
        setProcessingIRS(false);
      });
  }

  const updateStatusToProcessing = async () => {
    await client.apiIdverificationStatus(applicant.alloySessionId, application.id)
      .then(() => {
        return
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const checkDocVStatus = () => {
    if (
      sessionStorage.getItem("IRSDocVStatus")?.toLowerCase() == "submitted" ||
      sessionStorage.getItem("IRSDocVStatus")?.toLowerCase() == "completed" ||
      sessionStorage.getItem("IRSDocVStatus")?.toLowerCase() == "expired"
    ) {
      setProcessingIRS(true);
      updateStatusToProcessing();
      clearInterval(intervalId);
      submitIDVerification();
      sessionStorage.removeItem("IRSDocVStatus");
      setInitiatingIRS(false);
      navigate("/verify-id/upload-complete");
    }
  };

  const intervalId = setInterval(checkDocVStatus, 2000);

  useEffect(() => {
    document.title = "Let's verify your identity";
    dispatch(setLegalComplete(true));

    saveApplicationData();

    //setClickInsightEvents(application);
  }, []);

  const saveApplicationData = () => {
    client.apiApplicationsFetch().then((result) => {
      result.application = application;
      result.applicant = applicant;
      result.legalTerms = legalTerms;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });
  }

  useEffect(() => {
    saveApplicationData();
  }, [application]);

  return (
    <SlideTransition>
      <main aria-labelledby="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-72 max-md:items-center max-md:mt-20">
          <div className={style.raHeading}>
            <h1>Let's verify your identity</h1>
            <p className="sub-heading">
              We've made it easy to submit your ID. Here you will be able to
              take a photo of your ID, and then take a selfie, and we can verify
              you instantly!
            </p>
            <br></br>
            <b>Accepted ID Types</b>
            <ul style={{ listStyle: "disc", paddingLeft: 20, marginTop: 8 }}>
              <li>Valid Canadian Driver’s Licence</li>
              <li>Provincial or Territorial Photo ID Card</li>
              <li>Valid Canadian Passport</li>
              <li>Canadian Permanent Resident Card</li>
              <li>Certificate of Indian Status</li>
            </ul>
            <br></br>
            <p>
              Any images you send us will be retained for up to 90 days for
              identity confirmation and/or fraud prevention purposes. All image
              will have been securely disposed of once the 90 days have elapsed.
            </p>
            <div className={style.sectionContainer}>
              <div
                className={clsx(
                  "mt-5 flex-row-reverse",
                  style.consentContainer
                )}
              >
                <div className={style.scrollableContainer}>
                  <table className="mt-5">
                    <tbody>
                      <tr>
                        <th style={{ verticalAlign: "top" }}>
                          <p></p>
                        </th>
                        <th>
                          <p className="sub-heading  ml-3">
                            As part of the Meridian Credit Union Mortgage
                            application process, Meridian Credit Union may ask
                            you to provide personal information, such as your
                            name, residential address, date of birth, and other
                            personal information that will allow us to identify
                            you via Meridian Credit Union's Digital Identity
                            Verification Service. We may also request a copy of
                            your driver’s license or other identifying documents
                            ("ID"). We use this information to confirm your
                            identity during Meridian Credit Union Account and
                            Product openings, and to authenticate your identity
                            for the duration of your relationship with us. When
                            you submit a copy of your ID with a photo, we will
                            also ask you to take a short video selfie (together,
                            the “Submitted Images”). A short video selfie is
                            when we ask you to turn your head and say a sequence
                            of three numbers to confirm you are live and
                            present. When you do this, you authorize and
                            instruct Meridian Credit Union, including its
                            service provider Onfido, to use the Submitted Images
                            for the purpose of confirming your identity and
                            preventing fraud, including by comparing your selfie
                            to the photo or ID submitted to us. Once the
                            processing of the ID and selfie is completed, the
                            Submitted Images will be securely stored by Onfido
                            for a maximum period of 365 days, or until no longer
                            required in accordance with Meridian Credit Union’s
                            privacy statement. Meridian Credit Union may retain the
                            Submitted Images in our banking systems in order to
                            authenticate your identity in the future. We may
                            retain the Submitted Images for the duration of our
                            relationship with you. Although our agreement with
                            Onfido includes appropriate security safeguards,
                            Meridian Credit Union is not responsible for your
                            information stored with third parties. You
                            acknowledge and agree that Onfido may collect use,
                            disclose, retain or otherwise handle your personal
                            information for the purposes noted. The information
                            you submit through the Meridian Credit Union website
                            will be stored on servers inside or outside of
                            Canada. Information stored in Canada is subject to
                            Canadian law, and information stored outside of
                            Canada is subject to the laws of the jurisdiction
                            where the information is stored. (This includes
                            jurisdictions which may not have data protection
                            laws providing the same level of protection as
                            Canada). For additional information on Onfido’s
                            privacy policy; please visit -
                            https://onfido.com/privacy/ For any additional
                            questions; please contact Meridian Credit Union at
                            privacyofficer@ Meridian Credit Union.ca By
                            proceeding, you consent and agree to the collection,
                            usage, retention and disclosure of your personal
                            information as described above.
                          </p>
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="mt-5">
                <input
                  type="checkbox"
                  className="accent"
                  id="check1"
                  checked={consent}
                  onChange={handleConsent}
                />
                <label htmlFor="check1">
                  <h3 className="sub-heading ml-7 -mt-5">
                    I agree to the Meridian Credit Union Digital ID Verification
                    Terms and Privacy Statement
                  </h3>
                </label>
              </div>

              <div className="flex max-sm:hidden">
                <div className="mb-5 mr-5">
                  <button
                    type="submit"
                    onClick={() => {
                      handleClick();
                    }}
                    disabled={!consent || initiatingIRS || processingIRS}
                    className={clsx(
                      style.raHeading,
                      "mt-5 w-full sm:w-28 h-11 px-6",
                      !consent ? style.editMode : "primary"
                    )}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(initiatingIRS || processingIRS ? "" : "hidden")} />
                    Continue
                  </button>
                </div>
              </div>
              <div className="sm:hidden">
                <div className="mb-5">
                  <button
                    type="submit"
                    onClick={() => {
                      handleClick();
                    }}
                    disabled={!consent || initiatingIRS || processingIRS}
                    className={clsx(
                      style.raHeading,
                      "mt-5 w-full sm:w-28 h-11 px-6",
                      !consent ? style.editMode : "primary"
                    )}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(initiatingIRS || processingIRS ? "" : "hidden")} />
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SlideTransition>
  );
}
